// "New world" from an environment: a GLB the user made locally (uploaded
// here) or one they already have (their uploaded environments, or the
// environment of one of their own worlds). The world is created with that
// environment and the default sky, private to its owner.
import { validateFile } from '../config/aws';
import { putObject } from '../utils/uploadToStorage';
import { supabase } from '../config/supabase';
import { optimizeAsset } from './assetOptimizer';
import { PRESET_SKY } from './studioService';
import { AUTO_THUMB_MAX_BYTES, makeThumbnailFor } from '../utils/assetThumbnail';

export const DEFAULT_SKY_URL = PRESET_SKY.day;

export interface MyEnvironment {
  key: string;          // stable React key
  name: string;
  url: string;
  thumb: string | null;
  from: 'upload' | 'world';
}

/** The user's environments: uploads first, then ones inside their worlds (deduped by URL and name). */
export async function listMyEnvironments(userId: string): Promise<MyEnvironment[]> {
  const [uploads, worlds] = await Promise.all([
    supabase.from('user_uploads').select('id, name, url, thumbnail_url')
      .eq('user_id', userId).eq('type', 'environment').order('created_at', { ascending: false }).limit(60),
    supabase.from('worlds').select('id, name, thumbnail_url').eq('owner_id', userId).order('updated_at', { ascending: false }).limit(100),
  ]);
  const out: MyEnvironment[] = [];
  const seenUrl = new Set<string>(), seenName = new Set<string>();
  const add = (e: MyEnvironment) => {
    const n = e.name.toLowerCase();
    if (!e.url || seenUrl.has(e.url) || seenName.has(n)) return;
    seenUrl.add(e.url); seenName.add(n); out.push(e);
  };
  for (const u of uploads.data || []) {
    add({ key: `u-${u.id}`, name: (u.name || 'Environment').replace(/\.(glb|gltf)$/i, ''), url: u.url, thumb: u.thumbnail_url, from: 'upload' });
  }
  const ws = worlds.data || [];
  if (ws.length) {
    const { data: envs } = await supabase.from('world_assets').select('world_id, url, created_at')
      .eq('type', 'environment').in('world_id', ws.map((w) => w.id)).order('created_at', { ascending: true });
    const byWorld = new Map(ws.map((w) => [w.id, w]));
    for (const e of envs || []) {
      const w = byWorld.get(e.world_id);
      if (w) add({ key: `w-${w.id}-${e.url}`, name: w.name, url: e.url, thumb: w.thumbnail_url, from: 'world' });
    }
  }
  return out;
}

/** Upload a local .glb/.gltf to environments/ and file it in the user's library. */
export async function uploadEnvironment(file: File, onProgress?: (pct: number) => void): Promise<string> {
  const bad = validateFile(file, 'environment');
  if (bad) throw new Error(bad);
  const ext = file.name.split('.').pop()!.toLowerCase();
  const key = `environments/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '-')}`;
  const contentType = ext === 'gltf' ? 'model/gltf+json' : 'model/gltf-binary';
  const url = await putObject(key, file, contentType, { onProgress });

  // Library row + thumbnail + LODs are all best effort.
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const { data: row } = await supabase.from('user_uploads').insert({
      user_id: user.id, type: 'environment', name: file.name, url, file_size: file.size, mime_type: contentType,
      metadata: { key, extension: ext, uploadedAt: Date.now(), source: 'new-world' },
    }).select('id').single();
    if (row?.id && file.size <= AUTO_THUMB_MAX_BYTES) {
      makeThumbnailFor('user_uploads', row.id, file, file.name, contentType).catch(() => {});
    }
  }
  if (ext === 'glb') optimizeAsset(key).catch(() => {});
  return url;
}

/** A new private world holding the environment and the default sky; returns its id. */
export async function createWorldWithEnvironment(name: string, envUrl: string): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in first.');
  const stamp = Date.now();
  const { data, error } = await supabase.rpc('save_world', {
    p_world_id: null,
    p_world_name: name,
    p_owner_id: user.id,
    p_settings: { is_public: false, max_users: 20 },
    p_assets: [
      {
        type: 'environment', url: envUrl,
        metadata: { id: `env-${stamp}`, position: [0, 0, 0], scale: 1, rotation: [0, 0, 0], visible: true, isLocked: false },
      },
      {
        type: 'skybox', url: DEFAULT_SKY_URL,
        metadata: { id: `skybox-${stamp}`, filename: 'Default sky', uploadedAt: stamp, isCurrent: true },
      },
    ],
  });
  if (error) throw new Error(error.message || 'Could not create the world.');
  return data as string;
}
